import { useEffect, useState, type ReactElement } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDismissAppAnnouncement, usePendingAppAnnouncementState } from '@/features/users';
import { isEntryAuthFlowPath } from '@/shared/hooks/useAppAuthBootstrapFlow';
import { AppAnnouncementModal, welcomePostOnboardingAnnouncementKey } from './AppAnnouncementModal';
import type { AuthSession } from '@/auth/types';

type AppAnnouncementControllerProps = {
  activePath: string;
  forceWelcomeAnnouncement?: boolean;
  session: AuthSession | null;
};

const resolveDebugAnnouncementKey = (searchParams: URLSearchParams): string | null => {
  const announcementKey = searchParams.get('announcement');
  if (import.meta.env.DEV && announcementKey === welcomePostOnboardingAnnouncementKey) {
    return announcementKey;
  }
  return null;
};

export const AppAnnouncementController = ({
  activePath,
  forceWelcomeAnnouncement = false,
  session,
}: AppAnnouncementControllerProps): ReactElement | null => {
  const [searchParams] = useSearchParams();
  const debugAnnouncementKey = resolveDebugAnnouncementKey(searchParams);
  const [open, setOpen] = useState(false);
  const [debugOpenConsumed, setDebugOpenConsumed] = useState(false);
  const [forcedOpenConsumed, setForcedOpenConsumed] = useState(false);
  const isEntryAuthFlow = isEntryAuthFlowPath(activePath);
  const enabled = session?.onboardingCompleted === true && !isEntryAuthFlow;
  const pendingAnnouncementQuery = usePendingAppAnnouncementState({ enabled });
  const dismissAppAnnouncement = useDismissAppAnnouncement();
  const pendingAnnouncementKey = pendingAnnouncementQuery.data?.pendingAnnouncementKey ?? null;
  const forcedAnnouncementKey = forceWelcomeAnnouncement
    ? welcomePostOnboardingAnnouncementKey
    : null;
  const activeAnnouncementKey =
    debugAnnouncementKey ?? pendingAnnouncementKey ?? forcedAnnouncementKey;

  useEffect(() => {
    if (!debugAnnouncementKey) {
      setDebugOpenConsumed(false);
    }
  }, [debugAnnouncementKey]);

  useEffect(() => {
    if (!forceWelcomeAnnouncement) {
      setForcedOpenConsumed(false);
    }
  }, [forceWelcomeAnnouncement]);

  useEffect(() => {
    if (!session || isEntryAuthFlow) {
      setOpen(false);
      return;
    }
    if (debugAnnouncementKey && !debugOpenConsumed) {
      setOpen(true);
      setDebugOpenConsumed(true);
      return;
    }
    if (pendingAnnouncementKey) {
      setOpen(true);
      return;
    }
    if (forcedAnnouncementKey && !forcedOpenConsumed) {
      setOpen(true);
      setForcedOpenConsumed(true);
    }
  }, [
    debugAnnouncementKey,
    debugOpenConsumed,
    forcedAnnouncementKey,
    forcedOpenConsumed,
    isEntryAuthFlow,
    pendingAnnouncementKey,
    session,
  ]);

  const handleClose = (): void => {
    setOpen(false);
    if (debugAnnouncementKey) {
      return;
    }
    const dismissibleAnnouncementKey = pendingAnnouncementKey ?? forcedAnnouncementKey;
    if (dismissibleAnnouncementKey) {
      dismissAppAnnouncement.mutate(dismissibleAnnouncementKey);
    }
  };

  if (!session || isEntryAuthFlow || !activeAnnouncementKey) {
    return null;
  }

  return (
    <AppAnnouncementModal
      announcementKey={activeAnnouncementKey}
      open={open}
      onClose={handleClose}
    />
  );
};
